import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';

import {
  AgGridDropdownMenuCellRendererComponent,
  IAgGridDropdownMenuCellRendererParams,
} from 'v2/modules/shared/components/ag-grid-dropdown-menu-cell-renderer/ag-grid-dropdown-menu-cell-renderer.component';
import { AgGridValueFormatter } from 'v2/modules/shared/util/ag-grid-value-formatter.utils';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';

import {
  CSalesOrderStatusConstant,
  CSalesOrderStatusLabel,
} from '../../../../sales-order/interfaces/sales-order.interface';
import {
  EFulfillmentOrdersGridExpandableRowField,
  EFulfillmentOrdersGridField,
  IFulfillmentOrdersGridColumnDefContext,
  IFulfillmentOrdersGridOptionsContext,
} from '../types/fulfillment-orders-grid.types';
import { FulfillmentOrdersValueFormatterUtils } from '../utils/fulfillment-orders-value-formatter.utils';

const getColumnConfig = (
  context :IFulfillmentOrdersGridColumnDefContext,
) :ColDef[] => {
  return [
    {
      cellClass: 'lum-ag-expand-cell',
      cellRenderer: 'agGroupCellRenderer',
      filter: false,
      floatingFilter: false,
      headerClass: 'lum-ag-expand-cell lum-ag-expand-cell--header',
      lockPinned: true,
      lockPosition: true,
      maxWidth: 40,
      minWidth: 40,
      pinned: 'left',
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      suppressNavigable: true,
      width: 40,
    },
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      filter: false,
      filterParams: null,
      floatingFilter: false,
      lockPinned: true,
      lockPosition: true,
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      suppressNavigable: true,
      width: 40,
      maxWidth: 40,
      minWidth: 40,
      pinned: 'left',
    },
    {
      headerName: 'Fulfillment Order Key',
      field: EFulfillmentOrdersGridField.FULFILLMENT_ORDER_KEY,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Shipment Date',
      field: EFulfillmentOrdersGridField.SHIPMENT_DATE,
      filter: 'agDateColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
      },
      valueFormatter: AgGridValueFormatter.DATE,
    },
    {
      headerName: 'Qty Fulfilled',
      field: EFulfillmentOrdersGridField.QTY_SHIPPED,
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
      },
      valueFormatter: AgGridValueFormatter.DECIMAL_PLACES,
    },
    {
      headerName: 'Tracking Number',
      field: EFulfillmentOrdersGridField.TRACKING_NUMBER,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Fulfillment Warehouse Group',
      field: EFulfillmentOrdersGridField.FULFILLMENT_WAREHOUSE_GROUP,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Fulfillment Destination',
      field: EFulfillmentOrdersGridField.FULFILLMENT_DESTINATION,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Destination OMS Store',
      field: EFulfillmentOrdersGridField.DESTINATION_OMS_STORE,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Cost',
      field: EFulfillmentOrdersGridField.COST,
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
      },
      valueFormatter: AgGridValueFormatter.CURRENCY,
    },
    {
      headerName: 'Status',
      field: EFulfillmentOrdersGridField.STATUS,
      filterParams: {
        values: Object.values(CSalesOrderStatusConstant),
        valueFormatter: ({ value }) => {
          return CSalesOrderStatusLabel[value] || value;
        },
      },
      valueFormatter: ({ value }) => CSalesOrderStatusLabel[value] || value,
      filter: 'agSetColumnFilter',
    },
    {
      headerName: 'Channel Sync',
      field: EFulfillmentOrdersGridField.CHANNEL_SYNC,
      filter: false,
      filterParams: null,
      valueFormatter: (params :ValueFormatterParams) => {
        const latestActivity = params.data?.latestActivity;
        if (latestActivity) {
          if (latestActivity.properties?.error) {
            return 'Failed';
          }

          if (params.data?.pushedAt) {
            return 'Pushed';
          }
        }

        return 'Not Pushed';
      },
    },
    {
      headerName: 'Source Reference Identifier',
      field: EFulfillmentOrdersGridField.SOURCE_REFERENCE_IDENTIFIER,
      filter: 'agTextColumnFilter',
    },
    {
      filter: false,
      floatingFilter: false,
      lockPinned: true,
      lockPosition: true,
      maxWidth: 40,
      minWidth: 40,
      pinned: 'right',
      resizable: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      suppressNavigable: true,
      width: 40,
      cellRenderer: AgGridDropdownMenuCellRendererComponent,
      cellRendererParams: {
        options: context.dropdownOptions,
      } as IAgGridDropdownMenuCellRendererParams,
    },
  ] as ColDef[];
};

const getExpandableRowColumnDefs = (
  context :IFulfillmentOrdersGridOptionsContext,
) :ColDef[] => {
  return [
    {
      headerName: 'Ordered SKU',
      field: EFulfillmentOrdersGridExpandableRowField.ORDERED_SKU,
    },
    {
      headerName: 'Product Name',
      field: EFulfillmentOrdersGridExpandableRowField.PRODUCT_NAME,
    },
    {
      headerName: 'Est. Arrival Date',
      field: EFulfillmentOrdersGridExpandableRowField.EST_ARRIVAL_DATE,
      valueFormatter: (params :ValueFormatterParams) => {
        const arrivalDueDate =
          params.data?.salesOrderItem?.incomingPurchaseOrderRfq?.purchaseOrder
            ?.arrivalDueDate;
        const quantity =
          params.data?.salesOrderItem?.incomingPurchaseOrderRfq?.orderedQty;

        return arrivalDueDate && quantity
          ? FulfillmentOrdersValueFormatterUtils.dateValueFormatter(
              arrivalDueDate,
            ) +
              ' x' +
              FulfillmentOrdersValueFormatterUtils.decimalPlacesFormatter(quantity)
          : '';
      },
    },
    {
      headerName: 'Warehouse Group',
      field: EFulfillmentOrdersGridExpandableRowField.WAREHOUSE_GROUP,
      valueFormatter: (params :ValueFormatterParams) => {
        return params.data?.fulfillmentOrder?.fulfillmentWarehouseGroup;
      },
    },
    {
      headerName: 'Availability',
      field: EFulfillmentOrdersGridExpandableRowField.QTY_AVAILABLE,
      valueFormatter: (params :ValueFormatterParams) => {
        return FulfillmentOrdersValueFormatterUtils.decimalPlacesFormatter(
          params?.data?.availability ?? 0,
        );
      },
    },
    {
      headerName: 'Quantity',
      field: EFulfillmentOrdersGridExpandableRowField.QUANTITY,
    },
    {
      headerName: 'Lot',
      field: EFulfillmentOrdersGridExpandableRowField.LOT,
    },
    {
      headerName: 'UOM',
      field: EFulfillmentOrdersGridExpandableRowField.UOM,
      valueFormatter: (params :ValueFormatterParams) => {
        return params.data?.salesOrderItem?.rfq?.baseUnitOfMeasure?.name ?? '';
      },
    },
  ];
};

const getGridOptions = (
  context :IFulfillmentOrdersGridOptionsContext,
) :GridOptions => {
  return {
    rowSelection: 'multiple',
    rowModelType: 'serverSide',
    pagination: true,
    cacheBlockSize: 100,
    paginationPageSize: 100,
    suppressScrollOnNewData: true,
    defaultColDef: {
      sortable: true,
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: (params) => {
        const filterOptions = AgGridUtils.getFilterOptions(
          params.column.getColDef().filter,
        );

        return {
          filterOptions: filterOptions.options,
          defaultOption: filterOptions.default,
          maxNumConditions: 1,
          debounceMs: 1000,
        };
      },
      resizable: true,
      minWidth: 100,
    },
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    },
    masterDetail: true,
    detailCellRendererParams: {
      detailGridOptions: {
        columnDefs: getExpandableRowColumnDefs(context),
        defaultColDef: {
          flex: 1,
          resizable: false,
          sortable: false,
          suppressMenu: true,
        },
      },
      getDetailRowData: (params) => {
        if (
          !params.data.fulfillmentOrderItems ||
          params.data.fulfillmentOrderItems.length === 0
        ) {
          return context.fetchFulfillmentOrderItems(params.data.id);
        } else {
          params.successCallback(params.data.fulfillmentOrderItems);
        }
      },
    },
    onColumnMoved: context.saveGridState,
    onColumnResized: context.saveGridState,
    onFilterChanged: context.saveGridState,
    onSortChanged: context.saveGridState,
    onSelectionChanged: context.updateSelectedRows,
  };
};

export const FulfillmentOrdersGridConfig = {
  getColumnConfig,
  getGridOptions,
};
